import { useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { VerificationTemplateVersionStubs } from 'dashboard/models/simulate/stubs';
import { showMessage } from 'dashboard/redux/actions/messages';
import { getSandboxEnvironments } from 'dashboard/redux/selectors/organization';
import { RootState } from 'dashboard/redux/store';
import {
  createSandboxInquiry,
  loadInquiry,
  loadInquiryTemplateAndAccountTypesData,
  saveNextStepName,
  saveVerificationTemplateVersionStubs,
} from 'dashboard/redux/thunk-actions/inquiry-preview';
import { updateInquiryTemplate } from 'dashboard/redux/thunk-actions/inquiry-template';
import DashboardInquiryTemplatePreviewShowView from 'dashboard/views/DashboardInquiryTemplatePreviewShowView';
import { FieldValue } from 'lib/fields';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

import 'lib/initializers/i18n';

type Params = {
  inquiryTemplateId: string;
  inquiryTemplateVersionId: string;
};

const DashboardInquiryTemplatePreviewShowRoute = (props: PropsFromRedux) => {
  const { inquiryTemplateId, inquiryTemplateVersionId } = useParams<Params>() as Params; // Will always be defined

  const { loading: loadingInquiryTemplateAndAccountTypesData } = useApiLoader({
    loader: async () => {
      await props.loadInquiryTemplateAndAccountTypesData(inquiryTemplateVersionId);
    },
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load template for preview.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    callback: handleCreateSimulateInquiry,
    loading: createSimulateInquiryLoading,
    error: createSimulateInquiryError,
  } = useApiCallback(
    async ({
      inquiryTemplateId,
      inquiryTemplateVersionId,
      referenceId,
      accountTypeId,
      fields,
    }: {
      inquiryTemplateId: string;
      inquiryTemplateVersionId: string;
      referenceId?: string;
      accountTypeId?: string;
      fields?: Record<string, any>;
    }) => {
      if (props.currentEnvironment.type !== ApiModel.EnvironmentSandbox) {
        props.showMessage(
          'Preview is only available in Sandbox environments.',
          MessageType.Failure,
        );
        return;
      }

      await props.createSandboxInquiry({
        inquiryTemplateId,
        inquiryTemplateVersionId,
        referenceId,
        accountTypeId,
        fields,
        sandboxEnvId: props.currentEnvironment.id,
      });
      props.showMessage('Created inquiry for preview.', MessageType.Success);
    },
    [
      inquiryTemplateId,
      inquiryTemplateVersionId,
      props.currentEnvironment.type,
      props.currentEnvironment.id,
    ],
  );

  const { callback: handleLoadInquiry } = useApiCallback(
    async (inquiryId: string, abortSignal?: AbortSignal) => {
      if (props.currentEnvironment.type !== ApiModel.EnvironmentSandbox) {
        props.showMessage(
          'Preview is only available in Sandbox environments.',
          MessageType.Failure,
        );
        return;
      }

      await props.loadInquiry(inquiryId, props.currentEnvironment.id, abortSignal);
    },
    [props.currentEnvironment.type, props.currentEnvironment.id],
  );

  useApiErrorHandler({
    error: createSimulateInquiryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Could not create inquiry for preview. Please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: handleSkipToStep } = useApiCallback(
    async (stepName: string, inquiryId: string) => {
      if (props.currentEnvironment.type !== ApiModel.EnvironmentSandbox) {
        props.showMessage('Could not change step for previewed inquiry.', MessageType.Failure);
        return;
      }
      return await props.saveNextStepName(stepName, inquiryId, props.currentEnvironment.id);
    },
    [props.currentEnvironment.type, props.currentEnvironment.id],
  );

  const {
    callback: handleSaveVerificationTemplateVersionStubs,
    error: saveVerificationTemplateVersionStubsError,
  } = useApiCallback(
    async (
      inquiryId: string,
      verificationTemplateVersionStubs: VerificationTemplateVersionStubs[],
      fieldsStubs: Record<string, FieldValue>,
    ) => {
      if (props.currentEnvironment.type !== ApiModel.EnvironmentSandbox) {
        props.showMessage('Could not save simulated data for inquiry.', MessageType.Failure);
        return;
      }
      return await props.saveVerificationTemplateVersionStubs(
        inquiryId,
        props.currentEnvironment.id,
        verificationTemplateVersionStubs,
        fieldsStubs,
      );
    },
    [props.currentEnvironment.type, props.currentEnvironment.id],
  );

  useApiErrorHandler({
    error: saveVerificationTemplateVersionStubsError,
    onNetworkError: () => props.showMessage('Failed to set simulated data', MessageType.Failure),
    onError: () => props.showMessage('Failed to set simulated data', MessageType.Failure),
  });

  const handleViewInquiry = useCallback(() => {
    if (props.inquiry == null || props.currentEnvironment.type !== ApiModel.EnvironmentSandbox) {
      return;
    }
    window.open(`/dashboard/inquiries/${props.inquiry.id}`, '_blank');
  }, [props.inquiry, props.currentEnvironment.type]);

  return (
    <DashboardInquiryTemplatePreviewShowView
      inquiryTemplate={props.inquiryTemplate}
      inquiry={props.inquiry}
      inquiryTemplateVersion={props.inquiryTemplateVersion}
      inquiryTemplateVersionSchema={props.inquiryTemplateVersionSchema}
      accountTypesFilterOptions={props.accountTypesFilterOptions}
      loadingSimulateInquiry={createSimulateInquiryLoading}
      loadingInquiryTemplateAndAccountTypesData={loadingInquiryTemplateAndAccountTypesData}
      currentEnvironmentType={props.currentEnvironment.type}
      handleSaveVerificationTemplateVersionStubs={handleSaveVerificationTemplateVersionStubs}
      handleSkipToStep={handleSkipToStep}
      handleCreateSimulateInquiry={handleCreateSimulateInquiry}
      handleLoadInquiry={handleLoadInquiry}
      handleViewInquiry={handleViewInquiry}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  inquiryTemplate: state.inquiryPreview.inquiryTemplate,
  inquiryTemplateVersion: state.inquiryPreview.inquiryTemplateVersion,
  inquiryTemplateVersionSchema: state.inquiryPreview.inquiryTemplateVersionSchema,
  accountTypesFilterOptions: state.inquiryPreview.accountTypesFilterOptions,
  inquiry: state.inquiryPreview.inquiry,
  sandboxEnvironments: getSandboxEnvironments(state),
  currentEnvironment: state.currentEnvironment.currentEnvironment,
});

const mapDispatchToProps = {
  loadInquiryTemplateAndAccountTypesData,
  saveVerificationTemplateVersionStubs,
  saveNextStepName,
  createSandboxInquiry,
  loadInquiry,
  updateInquiryTemplate,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInquiryTemplatePreviewShowRoute);
